/**
 * MapEditor — Interaktiver Editor für Custom Maps (Player-Seite, MVP)
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 *
 * Workflow:
 *   const editor = new MapEditor(canvasEl, { subdivisions: 3 });
 *   editor.onChange((blockedFields, stats) => updateUI(stats));
 *   // User klickt Felder → toggle
 *   const blocked = editor.getBlockedFields();
 *   editor.destroy();
 *
 * Features:
 *   - Klick auf Feld → toggle blocked/unblocked
 *   - Kamera-Rotation funktioniert weiter (Drag auf leere Fläche)
 *   - Optional Mirror-Mode (Pol-Symmetrie via Y-Achse)
 *   - Live Stats (Counter + Warnung bei >40% blockiert)
 *   - Reuses GameRenderer — konsistente 3D-Darstellung
 */

import { Sphere } from './GameCore.js';
import { GameRenderer } from './GameRenderer.js';

const MAX_BLOCKED_RATIO = 0.4; // Soft-Warning-Schwelle
const STORAGE_KEY = 'boomfection-custom-maps';
const MAX_SAVED_MAPS = 5;

/**
 * Lädt die Liste gespeicherter Maps aus LocalStorage
 * @returns {Array<{id:string,name:string,subdivisions:number,blockedFields:number[],createdAt:number}>}
 */
export function loadSavedMaps() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(
            (m) =>
                m &&
                typeof m.id === 'string' &&
                typeof m.name === 'string' &&
                Number.isInteger(m.subdivisions) &&
                Array.isArray(m.blockedFields)
        );
    } catch {
        return [];
    }
}

/**
 * Speichert eine Map in LocalStorage (max MAX_SAVED_MAPS, älteste werden verdrängt)
 * @param {{name:string,subdivisions:number,blockedFields:number[]}} map
 * @returns {Array} Neue Liste
 */
export function saveMap(map) {
    const maps = loadSavedMaps();
    const entry = {
        id: `${Date.now().toString(36)}${Math.random().toString(36).substring(2, 6)}`,
        name: map.name.trim().slice(0, 40) || 'Unbenannt',
        subdivisions: map.subdivisions,
        blockedFields: Array.from(map.blockedFields),
        createdAt: Date.now()
    };
    maps.unshift(entry);
    const trimmed = maps.slice(0, MAX_SAVED_MAPS);
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } catch {
        /* LocalStorage voll oder disabled — silent fail */
    }
    return trimmed;
}

/**
 * Entfernt eine gespeicherte Map
 * @param {string} id
 * @returns {Array} Neue Liste
 */
export function deleteSavedMap(id) {
    const maps = loadSavedMaps().filter((m) => m.id !== id);
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(maps));
    } catch {
        /* silent */
    }
    return maps;
}

export class MapEditor {
    /**
     * @param {HTMLCanvasElement} canvas
     * @param {{ subdivisions?: number }} options
     */
    constructor(canvas, options = {}) {
        this.canvas = canvas;
        this.subdivisions = options.subdivisions || 3;
        this.mirrorMode = false;
        this.blockedFields = new Set();
        this._changeListeners = [];

        // Shift+Drag Paint State
        this._isPainting = false;
        this._paintMode = null; // 'block' | 'unblock'
        this._lastPaintedFieldId = null;

        this._buildSphere();

        // GameRenderer zeigt isBlocked Felder automatisch dunkel
        this.renderer = new GameRenderer(canvas);
        this.renderer.setGame(this.mockGame);
        this.renderer.onFieldClick = (fieldId) => this._toggleField(fieldId);

        this._setupPaintMode();
    }

    _setupPaintMode() {
        // Capture-Phase: läuft VOR GameRenderer's Handlers.
        // stopImmediatePropagation() verhindert Kamera-Rotation bei Shift+Drag.
        this._onMouseDown = (e) => {
            if (!e.shiftKey || e.button !== 0) return;
            e.stopImmediatePropagation();
            e.preventDefault();

            // Aktuelles Feld unter Cursor picken
            this.renderer.pickField(e.clientX, e.clientY, false);
            const fieldId = this.renderer.hoveredFieldId;
            if (fieldId == null) return;

            const field = this.sphere.fields.get(fieldId);
            if (!field) return;

            this._isPainting = true;
            this._paintMode = field.isBlocked ? 'unblock' : 'block';
            this._lastPaintedFieldId = fieldId;
            this._setBlocked(fieldId, this._paintMode === 'block');
            this.renderer.buildGeometry();
            this._emitChange();
        };

        this._onMouseMove = (e) => {
            if (!this._isPainting) return;
            e.stopImmediatePropagation();
            e.preventDefault();

            this.renderer.pickField(e.clientX, e.clientY, false);
            const fieldId = this.renderer.hoveredFieldId;
            if (fieldId == null || fieldId === this._lastPaintedFieldId) return;

            const field = this.sphere.fields.get(fieldId);
            if (!field) return;

            this._lastPaintedFieldId = fieldId;

            const shouldBlock = this._paintMode === 'block';
            if (field.isBlocked === shouldBlock) return; // Schon im Zielzustand

            this._setBlocked(fieldId, shouldBlock);
            this.renderer.buildGeometry();
            this._emitChange();
        };

        this._onMouseUp = () => {
            if (!this._isPainting) return;
            this._isPainting = false;
            this._paintMode = null;
            this._lastPaintedFieldId = null;
        };

        this.canvas.addEventListener('mousedown', this._onMouseDown, { capture: true });
        this.canvas.addEventListener('mousemove', this._onMouseMove, { capture: true });
        window.addEventListener('mouseup', this._onMouseUp, { capture: true });
    }

    setSubdivisions(subdivisions) {
        if (subdivisions === this.subdivisions) return;
        this.subdivisions = subdivisions;
        this.blockedFields.clear();
        this._buildSphere();
        this.renderer.setGame(this.mockGame);
        this._emitChange();
    }

    setMirrorMode(enabled) {
        this.mirrorMode = Boolean(enabled);
    }

    /**
     * Stellt eine gespeicherte Map wieder her
     * @param {{subdivisions:number,blockedFields:number[]}} data
     */
    loadFromData(data) {
        if (!data || !Array.isArray(data.blockedFields)) return;
        // Immer sauber starten: erst Subdivision setzen, dann alles clearen
        if (Number.isInteger(data.subdivisions) && data.subdivisions !== this.subdivisions) {
            this.setSubdivisions(data.subdivisions);
        }
        this.blockedFields.clear();
        for (const field of this.sphere.fields.values()) {
            field.isBlocked = false;
        }
        for (const id of data.blockedFields) {
            this._setBlocked(id, true);
        }
        this.renderer.buildGeometry();
        this._emitChange();
    }

    clear() {
        this.blockedFields.clear();
        for (const field of this.sphere.fields.values()) {
            field.isBlocked = false;
        }
        this.renderer.buildGeometry();
        this._emitChange();
    }

    /**
     * Invertiert die Selektion (freie Felder → blockiert und umgekehrt)
     */
    invert() {
        for (const field of this.sphere.fields.values()) {
            field.isBlocked = !field.isBlocked;
            if (field.isBlocked) {
                this.blockedFields.add(field.id);
            } else {
                this.blockedFields.delete(field.id);
            }
        }
        this.renderer.buildGeometry();
        this._emitChange();
    }

    /** @returns {number[]} Array der blockierten Feld-IDs */
    getBlockedFields() {
        return [...this.blockedFields];
    }

    getStats() {
        const total = this.sphere.fields.size;
        const blocked = this.blockedFields.size;
        return {
            total,
            blocked,
            ratio: total > 0 ? blocked / total : 0,
            overLimit: total > 0 && blocked / total > MAX_BLOCKED_RATIO
        };
    }

    /**
     * @param {(blockedFields: number[], stats: object) => void} callback
     */
    onChange(callback) {
        if (typeof callback === 'function') {
            this._changeListeners.push(callback);
        }
    }

    destroy() {
        if (this._onMouseDown) {
            this.canvas.removeEventListener('mousedown', this._onMouseDown, { capture: true });
            this.canvas.removeEventListener('mousemove', this._onMouseMove, { capture: true });
            window.removeEventListener('mouseup', this._onMouseUp, { capture: true });
        }
        if (this.renderer?.destroy) {
            this.renderer.destroy();
        }
        this.renderer = null;
        this._changeListeners = [];
    }

    // ============================================================
    // Internes
    // ============================================================

    _buildSphere() {
        this.sphere = new Sphere({ subdivisions: this.subdivisions });
        this.mockGame = {
            sphere: this.sphere,
            fields: this.sphere.fields,
            players: new Map(),
            config: {}
        };
    }

    _toggleField(fieldId) {
        const field = this.sphere.fields.get(fieldId);
        if (!field) return;
        this._setBlocked(fieldId, !field.isBlocked);
        this.renderer.buildGeometry();
        this._emitChange();
    }

    _setBlocked(fieldId, blocked) {
        const field = this.sphere.fields.get(fieldId);
        if (!field) return;
        field.isBlocked = blocked;
        if (blocked) {
            this.blockedFields.add(fieldId);
        } else {
            this.blockedFields.delete(fieldId);
        }

        if (this.mirrorMode) {
            const mirrorId = this._findMirrorField(field);
            if (mirrorId != null && mirrorId !== fieldId) {
                const mirrorField = this.sphere.fields.get(mirrorId);
                if (mirrorField) {
                    mirrorField.isBlocked = blocked;
                    if (blocked) {
                        this.blockedFields.add(mirrorId);
                    } else {
                        this.blockedFields.delete(mirrorId);
                    }
                }
            }
        }
    }

    /**
     * Findet das Y-gespiegelte Feld (Pol-Symmetrie)
     * @param {{center?: {x:number,y:number,z:number}}} field
     * @returns {number|null}
     */
    _findMirrorField(field) {
        if (!field.center) return null;
        const c = field.center;
        const tx = c[0], ty = -c[1], tz = c[2];
        let bestId = null;
        let bestDist = Infinity;
        for (const other of this.sphere.fields.values()) {
            if (!other.center) continue;
            const dx = other.center[0] - tx;
            const dy = other.center[1] - ty;
            const dz = other.center[2] - tz;
            // Squared distance — kein sqrt nötig, Vergleich reicht
            const dist = dx * dx + dy * dy + dz * dz;
            if (dist < bestDist) {
                bestDist = dist;
                bestId = other.id;
            }
        }
        // Toleranz: bei Einheitskugel liegt das echte Mirror-Feld bei dist < 0.01
        return bestDist < 0.1 ? bestId : null;
    }

    _emitChange() {
        const blockedFields = this.getBlockedFields();
        const stats = this.getStats();
        for (const cb of this._changeListeners) {
            cb(blockedFields, stats);
        }
    }
}
